import { useState, useEffect } from "react";
import { useToast } from "@chakra-ui/react";
import {
  Box,
  VStack,
  Heading,
  Text,
  Radio,
  RadioGroup,
  Skeleton,
  SimpleGrid,
  useColorModeValue,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { Line } from "react-chartjs-2";
import useAxios from "../../utils/useAxios";

interface IFlowPoint {
  flow_l_per_min: number;
  created_at: string;
}
interface IFlowResponse {
  current?: {
    flow_l_per_min: number;
    created_at: string;
  };
  history?: IFlowPoint[];
}

const fullDateTimeFormat: Intl.DateTimeFormatOptions = {
  year: "2-digit",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
};

const hourlyDateTimeFormat: Intl.DateTimeFormatOptions = {
  year: "2-digit",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
};

const dateOnlyFormat: Intl.DateTimeFormatOptions = {
  year: "2-digit",
  month: "2-digit",
  day: "2-digit",
};

const DevicePlotValve = () => {
  const api = useAxios();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();

  const [days, setDays] = useState<number>(7);
  const [flowLoading, setFlowLoading] = useState<boolean>(true);
  const [flowData, setFlowData] = useState<IFlowResponse | null>(null);

  const getDateFormat = (): Intl.DateTimeFormatOptions => {
    if (days > 30) return dateOnlyFormat;
    if (days > 1) return hourlyDateTimeFormat;
    return fullDateTimeFormat;
  };

  const fetchFlow = async () => {
    setFlowLoading(true);
    try {
      const response = await api.get<IFlowResponse>(`frontend/devices/flow-data/${id}/`, {
        params: { days },
      });
      setFlowData(response.data);
    } catch {
      setFlowData(null);
      toast({
        title: "Error fetching flow data",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setFlowLoading(false);
    }
  };

  useEffect(() => {
    fetchFlow();
  }, [days, id]);

  const heading_color = useColorModeValue("black", "gray.200");

  const formattedLabels =
    flowData?.history?.map((point) => {
      const d = new Date(point.created_at);
      return d.toLocaleDateString("nb-NO", getDateFormat());
    }) || [];

  const flowValues =
    flowData?.history?.map((point) => Number(point.flow_l_per_min.toFixed(3))) || [];

  return (
    <Box>
      <Heading as="h2" size="lg" mb={4} color={heading_color}>
        Flow History
      </Heading>

      <SimpleGrid minChildWidth="300px" spacing="20px" mb={4}>
        <RadioGroup onChange={(value: string) => setDays(Number(value))} value={days.toString()}>
          <Radio mr={3} value={"1"}>
            1 day
          </Radio>
          <Radio mr={3} value={"7"}>
            7 days
          </Radio>
          <Radio mr={3} value={"30"}>
            30 days
          </Radio>
          <Radio mr={3} value={"90"}>
            90 days
          </Radio>
          <Radio value={"365"}>365 days</Radio>
        </RadioGroup>
      </SimpleGrid>

      {flowLoading ? (
        <Skeleton height="300px" width="100%" />
      ) : (
        <>
          {flowData?.history && flowData.history.length > 0 ? (
            <Box pos="relative" w="100%" h="300px">
              <Line
                data={{
                  labels: formattedLabels,
                  datasets: [
                    {
                      label: "Flow (L/min)",
                      data: flowValues,
                      fill: true,
                      backgroundColor: "rgba(100,150,250,0.2)",
                      borderColor: "rgba(100,150,250,1)",
                      pointRadius: 2,
                      tension: 0.2,
                    },
                  ],
                }}
                options={{
                  maintainAspectRatio: false,
                  layout: {
                    padding: { top: 8, bottom: 8, left: 4, right: 4 },
                  },
                  scales: {
                    x: {
                      ticks: {
                        autoSkip: true,
                        maxTicksLimit: 6,
                        maxRotation: 0,
                        padding: 4,
                        font: { size: 10 },
                      },
                      grid: { display: false },
                    },
                    y: {
                      beginAtZero: true,
                      ticks: {
                        autoSkip: true,
                        maxTicksLimit: 4,
                        padding: 4,
                        font: { size: 10 },
                      },
                    },
                  },
                  plugins: {
                    legend: {
                      display: true,
                      position: "top",
                      labels: { boxWidth: 12, padding: 8 },
                    },
                    tooltip: { intersect: false, mode: "index" },
                  },
                }}
              />
            </Box>
          ) : (
            <Text fontSize="sm" color="gray.500">
              No flow history
            </Text>
          )}
        </>
      )}
    </Box>
  );
};

export default DevicePlotValve;
